import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  buildLocationTree,
  collectProviders,
  locationLabel,
  ratePoint,
  type LocationNode,
  type LocationTreeNode,
  type Measurement,
  type MeasurementPoint,
  type MeasurementPointWriteRequest,
  type MeasurementSeries,
} from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { listLocations } from '../api/locations';
import { getMeasurementSeries, saveMeasurementPoint } from '../api/measurements';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { Layout } from '../components/Layout';
import { RatingLegend } from '../components/RatingLegend';
import { RatingMatrix } from '../components/RatingMatrix';
import { NotFoundPage } from './NotFoundPage';

const LOCATION_NOTE_MAX_LENGTH = 200;

interface LocationOption {
  id: string;
  label: string;
  depth: number;
}

function flattenLocations(entries: readonly LocationTreeNode[], depth = 0): LocationOption[] {
  return entries.flatMap((entry) => [
    { id: entry.node.id, label: locationLabel(entry.node), depth },
    ...flattenLocations(entry.children, depth + 1),
  ]);
}

/** `2026-01-15` -> `15.01.2026`; anything else is shown unchanged. */
function formatDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : isoDate;
}

/**
 * Local device time `2026-01-15T09:05:07` -> `15.01.2026 09:05`. Formatted from
 * the string on purpose: no `Date`, so no zone conversion can creep in.
 */
function formatMeasuredAt(measuredAt: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(measuredAt);
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}` : measuredAt;
}

const orDash = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? '---' : String(value);

type RowStatus = { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string };

/**
 * One measurement series: points with their traffic-light matrix, location
 * assignment and a cell list per measurement. Ratings are computed at render
 * time and never stored.
 */
export function MeasurementSeriesDetailPage(): ReactNode {
  const { session } = useSession();
  const { customerId: customerIdParam, seriesId } = useParams<{
    customerId: string;
    seriesId: string;
  }>();
  const enabled = session?.status === 'authenticated' && session.me.features?.measurements === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [series, setSeries] = useState<MeasurementSeries | undefined>(undefined);
  const [points, setPoints] = useState<MeasurementPoint[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!customerIdParam || !seriesId || !enabled) return;
    let cancelled = false;
    Promise.all([
      getCustomer(customerIdParam),
      listLocations(customerIdParam),
      getMeasurementSeries(customerIdParam, seriesId),
    ])
      .then(([customer, locationResponse, detail]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        setSeries(detail.series);
        setPoints(detail.points);
        setMeasurements(detail.measurements);
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Messreihe konnte nicht geladen werden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [customerIdParam, seriesId, enabled]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam || !seriesId) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite = me.permissions.includes('measurements:write');
  const editable = canWrite && series?.status === 'complete';
  const locationOptions = flattenLocations(buildLocationTree(locations));

  const technologies = series?.technologies ?? [];
  const providers = collectProviders(measurements);
  const sortedPoints = [...points].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  const measurementsByPoint = new Map<string, Measurement[]>();
  for (const entry of measurements) {
    const list = measurementsByPoint.get(entry.measurementPointId) ?? [];
    list.push(entry);
    measurementsByPoint.set(entry.measurementPointId, list);
  }

  async function savePoint(
    point: MeasurementPoint,
    change: { locationId?: string; locationNote?: string },
  ): Promise<void> {
    const locationId = 'locationId' in change ? change.locationId : point.locationId;
    const locationNote = 'locationNote' in change ? change.locationNote : point.locationNote;
    const write: MeasurementPointWriteRequest = {
      ...(locationId ? { locationId } : {}),
      ...(locationNote ? { locationNote } : {}),
      ...(point._etag ? { ifMatchEtag: point._etag } : {}),
    };
    setRowStatus((current) => ({ ...current, [point.id]: { kind: 'saving' } }));
    try {
      const saved = await saveMeasurementPoint(customerId, point.id, write);
      setPoints((current) => current.map((entry) => (entry.id === saved.id ? saved : entry)));
      setRowStatus((current) => ({ ...current, [point.id]: { kind: 'saved' } }));
    } catch (caught: unknown) {
      const message = caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.';
      setRowStatus((current) => ({ ...current, [point.id]: { kind: 'error', message } }));
    }
  }

  const statusText = (id: string): string => {
    const status = rowStatus[id];
    if (!status) return '';
    if (status.kind === 'saving') return 'speichert ...';
    return status.kind === 'saved' ? 'gespeichert' : status.message;
  };

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        <div className="card">
          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : !series ? (
            <p className="muted" role="status" aria-live="polite">
              Messreihe wird geladen ...
            </p>
          ) : (
            <>
              <h1>{series.label}</h1>
              <p className="muted">
                {formatDate(series.measuredOn)} · {series.pointCount} Punkte
              </p>
              <RatingLegend />
              {series.status === 'importing' ? (
                <p className="notice notice--warning">
                  Import unvollständig – löschen und neu importieren
                </p>
              ) : null}

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Nr.</th>
                      <th scope="col">Zeitpunkt</th>
                      <th scope="col">Ort</th>
                      <th scope="col">Ortsfeld</th>
                      <th scope="col">Ampel</th>
                      <th scope="col">Zellen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPoints.map((point) => {
                      const own = measurementsByPoint.get(point.id) ?? [];
                      const cells = own.flatMap((entry) => entry.cells);
                      const ratings = ratePoint(cells, technologies, providers);
                      const open = expanded[point.id] === true;
                      const saving = rowStatus[point.id]?.kind === 'saving';
                      const locationName = locationOptions.find(
                        (option) => option.id === point.locationId,
                      )?.label;
                      return (
                        <Fragment key={point.id}>
                          <tr className="point-row">
                            <td>{point.sequenceNumber}</td>
                            <td>{own[0] ? formatMeasuredAt(own[0].measuredAt) : '---'}</td>
                            <td>
                              {editable ? (
                                <select
                                  aria-label={`Ort für Punkt ${point.sequenceNumber}`}
                                  value={point.locationId ?? ''}
                                  disabled={saving}
                                  onChange={(event) =>
                                    void savePoint(point, {
                                      locationId: event.target.value || undefined,
                                    })
                                  }
                                >
                                  <option value="">(kein Ort)</option>
                                  {locationOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {'— '.repeat(option.depth)}
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                (locationName ?? '---')
                              )}
                            </td>
                            <td>
                              {editable ? (
                                <input
                                  type="text"
                                  aria-label={`Ortsfeld für Punkt ${point.sequenceNumber}`}
                                  maxLength={LOCATION_NOTE_MAX_LENGTH}
                                  defaultValue={point.locationNote ?? ''}
                                  disabled={saving}
                                  onBlur={(event) => {
                                    const value = event.target.value.trim();
                                    if (value !== (point.locationNote ?? '')) {
                                      void savePoint(point, { locationNote: value });
                                    }
                                  }}
                                />
                              ) : (
                                (point.locationNote ?? '---')
                              )}
                              <span className="muted" role="status" aria-live="polite">
                                {' '}
                                {statusText(point.id)}
                              </span>
                            </td>
                            <td>
                              <RatingMatrix
                                ratings={ratings}
                                technologies={technologies}
                                providers={providers}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="button button--ghost"
                                aria-expanded={open}
                                onClick={() =>
                                  setExpanded((current) => ({ ...current, [point.id]: !open }))
                                }
                              >
                                Zellen
                              </button>
                            </td>
                          </tr>
                          {open ? (
                            <tr className="cells-row">
                              <td colSpan={6}>
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th scope="col">Technologie</th>
                                      <th scope="col">Betreiber</th>
                                      <th scope="col">Zellen-ID</th>
                                      <th scope="col">Band</th>
                                      <th scope="col">PCI</th>
                                      <th scope="col">RSRP</th>
                                      <th scope="col">RSRQ</th>
                                      <th scope="col">SINR</th>
                                      <th scope="col">dBm</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cells.map((cell, index) => (
                                      <tr
                                        key={`${cell.technology}|${cell.provider}|${cell.cellId}|${index}`}
                                      >
                                        <td>{cell.technology}</td>
                                        <td>{cell.provider}</td>
                                        <td>{cell.cellId}</td>
                                        <td>{orDash(cell.band)}</td>
                                        <td>{orDash(cell.pci)}</td>
                                        <td>{orDash(cell.rsrp)}</td>
                                        <td>{orDash(cell.rsrq)}</td>
                                        <td>{orDash(cell.sinr)}</td>
                                        <td>{orDash(cell.dbm)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
